import { Drawer, useTableRowSelection } from '@/components';
import { useExpenditureBudgetPurpose } from '@/features/master-table/hooks';
import { getExpenditureBudgetPurposeConcatData } from '@/features/mold/utils';
import {
  useCreateExpenditureItem,
  useUpdateExpenditureItem,
} from '@/features/project/projects/detail/income-and-expenditure/_hooks/expenditure-item';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import type { SubProjectsModel } from '../../../../_models/sub-project';
import { useProjectDetailDrawerState } from '../../../_components/ProjectDrawerStateProvider';
import { ExpenditureItemForm } from './ExpenditureItemForm';

type Props = {
  fiscalYear: number;
  data?: z.infer<typeof SubProjectsModel.element> | undefined;
};

export const ExpenditureItemFormDrawer: FC<Props> = ({ fiscalYear, data }) => {
  const { t } = useTranslation();
  const { id: projectId } = useId();
  const { openMessage } = useMessage();
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { mutateAsync: createExpenditureItemAsync } =
    useCreateExpenditureItem();
  const { mutateAsync: updateExpenditureItemAsync } =
    useUpdateExpenditureItem();

  const { clearAllSelections } = useTableRowSelection();

  const openDrawer =
    drawerState.open && drawerState.type === 'expenditure-item';
  const isEditing = openDrawer && drawerState.isEditing;

  const expenditureCart = openDrawer
    ? data?.expenditureCarts.find(
        (cart) => cart.id === drawerState.expenditureCartId,
      )
    : undefined;

  const expenditureItem = isEditing
    ? expenditureCart?.expenditureItems.find(
        (item) => item.id === drawerState.expenditureItemId,
      )
    : undefined;

  const { data: majorExpenditureBudgetPurposeData } =
    useExpenditureBudgetPurpose({
      id: expenditureItem?.expenditureBudgetPurposeId ?? '',
    });

  const accountingType = expenditureCart?.accountingType;

  const initialExpenditureBudgetPurposeConstituentIds = useMemo(() => {
    if (majorExpenditureBudgetPurposeData) {
      if (!isEditing) {
        return undefined;
      }
      return getExpenditureBudgetPurposeConcatData({
        data: majorExpenditureBudgetPurposeData,
        withoutAccountingName: true,
      }).ids;
    }
    return expenditureItem?.budgetType.type === 'initial' ||
      expenditureItem?.budgetType.type === 'supplementary'
      ? [
          expenditureItem.expenditureBudgetPurpose?.jurisdictionId ?? '',
          (expenditureCart?.accountingType.type === 'general'
            ? expenditureItem.expenditureBudgetPurpose?.organizationId
            : expenditureItem.expenditureBudgetPurpose?.accountTitleId) ?? '',
          expenditureItem.expenditureBudgetPurpose?.expenditurePurpose
            .upperId ?? '',
          expenditureItem.expenditureBudgetPurposeId ?? '',
        ].filter(Boolean)
      : undefined;
  }, [
    expenditureCart?.accountingType.type,
    expenditureItem?.budgetType.type,
    expenditureItem?.expenditureBudgetPurpose?.accountTitleId,
    expenditureItem?.expenditureBudgetPurpose?.expenditurePurpose.upperId,
    expenditureItem?.expenditureBudgetPurpose?.jurisdictionId,
    expenditureItem?.expenditureBudgetPurpose?.organizationId,
    expenditureItem?.expenditureBudgetPurposeId,
    isEditing,
    majorExpenditureBudgetPurposeData,
  ]);

  return (
    <Drawer
      open={openDrawer && !!accountingType}
      onClose={() => {
        setDrawerState({ open: false });
        clearAllSelections();
      }}
      destroyOnHidden
    >
      <ExpenditureItemForm
        fiscalYear={fiscalYear}
        accountingType={accountingType}
        accountTitleId={expenditureCart?.accountTitleId}
        initialValues={{
          ...expenditureItem,
          budgetType: expenditureItem?.budgetType.type,
          expenditureBudgetPurposeConstituentIds:
            initialExpenditureBudgetPurposeConstituentIds,
        }}
        existBudgetTypeIds={expenditureCart?.expenditureItems.map(
          (item) => item.budgetTypeId,
        )}
        onSubmit={async ({ values, nextDrawerKey, onSuccess }) => {
          invariant(openDrawer, t('unreachable-code'));

          if (drawerState.isEditing && data && expenditureCart) {
            await updateExpenditureItemAsync({
              ...values,
              ...drawerState,
              id: drawerState.expenditureItemId,
              projectId,
              subProjectId: data.id,
              accountingType: expenditureCart.accountingType.type,
            });
          } else {
            invariant(data && expenditureCart, t('unreachable-code'));
            await createExpenditureItemAsync({
              ...values,
              ...drawerState,
              projectId,
              subProjectId: data.id,
              accountingType: expenditureCart.accountingType.type,
            });
          }
          await Promise.all([
            openMessage(
              'success',
              drawerState.isEditing
                ? t('message-update-completed')
                : t('add-completed'),
            ),
            (async () => {
              if (!nextDrawerKey) {
                setDrawerState({ open: false });
              }
            })(),
            (async () => {
              onSuccess?.();
            })(),
          ]);
        }}
        isEditing={isEditing}
      />
    </Drawer>
  );
};
